/**
 * The seed of an ephemeral open. Ephemeral sheets always get an in-memory
 * backend: their uuid rows do not exist server-side, so the real backend
 * refuses every cell fetch and write against them (rows with a lookupKey
 * still reach the server, through the client's rows backend). The seed lands
 * in the client's ordinary row caches, the ones the sheet's accessor resolves
 * (`sharedRowCache`), so the seeded cells and their coverage are what its
 * first load finds.
 */
import { nonnull } from "@tento-core/assert";
import { Json, RawSheet, RowKey, RowNetworkKey } from "@tento-lona/sheets";
import type { CellCardinality } from "@tento-lona/sheets/platform";
import type { SheetModelCache } from "@tento-lona/sheets/internal-sdk";

import { SheetRowMeta } from "../rows/sheet-row-meta.ts";
import { SeededSyncBackend } from "../sync/sync-backend.ts";
import { checkSeedDates, type EphemeralSheets } from "./ephemeral-spec.ts";

/** What the seed reads from the opening client. */
export type EphemeralSeedContext = Readonly<{
  /** The seeded backend's construction: principal, real backend, listener errors, shared row cache. */
  backend: ConstructorParameters<typeof SeededSyncBackend>;
  /** The client's registries, which declare each seeded row's cardinality. */
  registries: SheetModelCache["registries"];
  /** Holds a row in the client's evictable ordinary store; the returned function releases it. */
  hold: (key: RowKey) => () => void;
}>;

/**
 * Seeds `ephemeral`'s rows with data into a fresh `SeededSyncBackend` and runs
 * `open` with it. The seed writes into the client's evictable ordinary store
 * before the sheet pins its rows (at parse): each seeded row is held until
 * `open` returns, so seeding past the store's capacity never evicts an earlier
 * seeded row (whose cells the first load would then fetch and install again).
 */
export function withEphemeralSeed<T>(
  cx: EphemeralSeedContext,
  ephemeral: EphemeralSheets.EphemeralSheet,
  open: (seeded: SeededSyncBackend) => T,
): T {
  // A seed date that is no partial date is a typed refusal naming the row,
  // not a chrono parse failure from inside the seed.
  checkSeedDates(ephemeral);
  const [principal] = cx.backend;
  const seeded = new SeededSyncBackend(...cx.backend);
  const rowsWithData = ephemeral.rows.filter((r) => r.data && r.data.length > 0);
  const seedPins = rowsWithData.map((row) => cx.hold(RowKey.of(row.id.toString())));
  try {
    for (const row of rowsWithData) {
      // The row's metadata first: its cardinality is declared on the shared
      // cache before the seed's first cell creates the row's store, which
      // would otherwise take the `single` default and refuse the hydrate's
      // `multi` declaration. The cardinality is the one the hydrate
      // declares, the sheet descriptor's (a `list` or `calendar-row` row is
      // `multi` there whatever its row type's default says).
      const attributes = Json.object(row.attributes ?? null);
      seeded.storeRowMeta(row.id, SheetRowMeta.fromFields({
        id: row.id,
        type: row.type,
        ownerId: principal,
        timeScale: row.timeScale,
        attributes: row.attributes ?? null,
        formula: row.formula ?? null,
        heightPx: row.heightPx ?? null,
        icon: row.icon ?? null,
        label: row.label ?? null,
        lookupKey: row.lookupKey ?? null,
        metadata: null,
      }), seededCardinality(RawSheet.toDescriptor({
        id: RowNetworkKey.fromWire(row.id.toString()),
        type: row.type,
        ...(attributes == null ? {} : { attributes }),
        ...(row.lookupKey == null ? {} : { lookupKey: row.lookupKey }),
      }, cx.registries)));
      seeded.seedRow(row.id, nonnull(row.data, "withEphemeralSeed: filtered to rows with data"));
    }
    return open(seeded);
  } finally {
    // The parsed sheet pins its own stored rows; the seed's holds go.
    for (const release of seedPins) release();
  }
}

/** A seeded row's cell cardinality as the hydrate declares it: a source descriptor's, else the `single` default. */
function seededCardinality(descriptor: ReturnType<typeof RawSheet.toDescriptor>): CellCardinality {
  return descriptor.role === "source" ? descriptor.cellCardinality : "single";
}
